import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Search, Loader2, X } from "lucide-react";
import { CaseDrawer } from "@/components/CaseDrawer";
import { useAuth } from "@/context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export function GlobalSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const canManage = ["admin", "ops_manager"].includes(user.role);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openCase, setOpenCase] = useState(null);
  const box = useRef(null);
  const inputRef = useRef(null);
  const timer = useRef(null);
  const requestId = useRef(0);

  useEffect(() => {
    const onDoc = (e) => {
      if (box.current && !box.current.contains(e.target)) {
        setResults(null);
        setMobileOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") { setResults(null); setMobileOpen(false); }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKeyDown);
      clearTimeout(timer.current);
      requestId.current += 1;
    };
  }, []);

  useEffect(() => { setMobileOpen(false); setResults(null); }, [location.pathname]);
  useEffect(() => { if (mobileOpen) inputRef.current?.focus(); }, [mobileOpen]);

  const onChange = (value) => {
    setQ(value);
    clearTimeout(timer.current);
    const next = ++requestId.current;
    if (value.trim().length < 2) { setResults(null); setLoading(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/workflow/search?q=${encodeURIComponent(value.trim())}`);
        if (next === requestId.current) setResults(data.results || []);
      } catch {
        if (next === requestId.current) setResults([]);
      } finally {
        if (next === requestId.current) setLoading(false);
      }
    }, 300);
  };

  const dismissMobile = () => { setMobileOpen(false); setResults(null); };

  return (
    <div className="relative shrink-0" ref={box}>
      <button type="button" className="inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground hover:bg-muted sm:hidden"
        data-testid="global-search-mobile-trigger" aria-label="Search leads and policies"
        aria-expanded={mobileOpen} aria-controls="global-search-panel"
        onClick={() => setMobileOpen(value => !value)}>
        <Search className="h-5 w-5" />
      </button>
      <div id="global-search-panel" className={mobileOpen
        ? "fixed left-3 right-3 top-16 z-50 min-w-0 sm:static sm:block"
        : "hidden sm:static sm:block"}>
        <div className="flex min-w-0 items-center gap-2 rounded-lg border bg-background px-3 py-2 shadow-lg sm:rounded-full sm:bg-white sm:py-1.5 sm:shadow-none">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input ref={inputRef} value={q} onChange={(e) => onChange(e.target.value)}
            placeholder={t("search.placeholder")} aria-label="Search leads and policies"
            className="w-full min-w-0 bg-transparent text-sm outline-none sm:w-56"
            data-testid="global-search-input" />
          {loading && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />}
          <button type="button" onClick={dismissMobile}
            aria-label="Close search" className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md sm:hidden"
            data-testid="global-search-mobile-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        {results && (
          <div className="absolute left-0 right-0 top-12 z-50 max-h-[min(24rem,70dvh)] overflow-y-auto rounded-lg border bg-card p-1 shadow-xl sm:left-auto sm:right-0 sm:top-11 sm:w-96"
            data-testid="global-search-results">
            {results.length === 0
              ? <div className="p-3 text-xs text-muted-foreground">{t("search.no_matches")}</div>
              : results.map((result, index) => (
                <button key={index} type="button"
                  onClick={() => {
                    if (result.type === "lead" && result.lead_id) {
                      setOpenCase(result.lead_id); dismissMobile();
                    } else if (result.type === "policy") {
                      dismissMobile();
                      navigate(`/policies?search=${encodeURIComponent(result.policy_number || result.display_id || result.client_name || "")}`);
                    }
                  }}
                  data-testid={`gs-result-${index}`}
                  className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-muted">
                  <div className="min-w-0 text-left">
                    <div className="truncate font-medium">{result.client_name}</div>
                    <div className="break-words text-[11px] text-muted-foreground">
                      {result.type} · {result.canonical || "—"}
                      {result.policy_number ? ` · ${result.policy_number}` : ""}
                      {result.carrier ? ` · ${result.carrier}` : ""}
                    </div>
                  </div>
                  <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase">{result.type}</span>
                </button>
              ))}
            <div className="border-t px-3 py-1.5 text-[10px] text-muted-foreground">{t("search.hint")}</div>
          </div>
        )}
      </div>
      {openCase && <CaseDrawer leadId={openCase} onClose={() => setOpenCase(null)} canManage={canManage} />}
    </div>
  );
}
